import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  Paper,
  Box,
  Typography,
  Button,
  Grid,
  Dialog,
  Container,
  Stack,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  AttachMoney as MoneyIcon,
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Header from "../Header";
import { GridPagination, DataGrid } from '@mui/x-data-grid';
import { NumericFormat } from "react-number-format";
import { dateDifference, formatDate, formatDateText } from "../CommonFunctions";
import { parseISO } from "date-fns";
import { Link } from "react-router-dom";
import { makeStyles } from '@mui/styles';
import MakePayment from "../MakePayment";

const useStyles = makeStyles((theme) => ({
  duplicateRow: {
    backgroundColor: '#ffcccc',
    '&:hover': {
      backgroundColor: '#c99393 !important',
    },
  },
  noEntryRow: {
    backgroundColor: '#b6e6fa',
    '&:hover': {
      backgroundColor: '#8ed6f5 !important',
    },
  },
  refundedRow: {
    backgroundColor: '#b3db97',
    '&:hover': {
      backgroundColor: '#9adb6b !important',
    },
  },
}));

const options = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
let totalCash = 0;
let totalOther = 0;
let totalCreditAmount = 0;
const now = new Date();
now.setDate(1);

const CashCollectionReport = () => {
  const currentDate = formatDate(new Date());
  const [searchStartDate, setSearchStartDate] = useState(formatDate(now));
  const [searchEndDate, setSearchEndDate] = useState(formatDate(new Date()));
  const [reportSummary, setReportSummary] = useState([]);
  const [missingPaymentsReportSummary, setMissingPaymentsReportSummary] = useState([]);
  const [makePaymentModal, setMakePaymentModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [sortModel, setSortModel] = useState([]);
  const [missingReceiptWarning, setMissingReceiptWarning] = useState(false);
  const [displayType, setDisplayType] = useState("daily");
  let totalExemptAmount = 0, totalTaxableAmount = 0, totalSalesTax = 0, totalTourismTax = 0;
  let salesTax = 0.07;
  let tourismTax = 0.05;
  const roomsToTotalsDict = {};

  const url = process.env.REACT_APP_SERVER_URL;

  useEffect(() => {
    getPaymentDetailsByDateRange(searchStartDate, searchEndDate);
  }, []);

  const columns = [
    { field: 'roomid', headerName: 'Room Number', width: 80, align: 'left' },
    {
      field: 'paymentId', headerName: 'Payment Id', width: 100, align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        if (currentRow.paymentId)
          return currentRow.paymentId
        return "No Payment";
      }
    },
    {
      field: 'receiptNumber', headerName: 'Receipt Number', width: 120, align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <Link
          onMouseUp={() => { { setMakePaymentModal(false) } }}
          onTouchMove={() => { { setMakePaymentModal(false) } }}
          onClick={(e) => {
            setMakePaymentModal(true);
            setSelectedRow(params.row);
          }}>
          {currentRow.receiptNumber}
        </Link>
      }
    },
    { field: 'paidBy', headerName: 'Paid By', width: 180, align: 'left' },
    {
      field: 'actualPaymentDate', headerName: 'Payment Date', width: 140, align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        if (currentRow.actualPaymentDate)
          return parseISO(currentRow.actualPaymentDate).toLocaleDateString('en-us', options)
        return null;
      }
    },
    {
      field: 'checkin', headerName: 'Tenancy Period', width: 220, align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <Link to={`/tenants/add/${currentRow.id}`} target="_blank">
          {parseISO(currentRow.checkin).toLocaleDateString('en-us', options)
            + '  -  ' +
            parseISO(currentRow.checkout).toLocaleDateString('en-us', options)
          }
        </Link>
      }
    },
    {
      field: 'rate', headerName: 'Rent', width: 90, align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <NumericFormat value={currentRow.rate} thousandSeparator prefix={'$'} displayType="text" style={{ color: "grey" }} />
      }
    },
    {
      field: 'amount', headerName: 'Total', width: 90, align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <NumericFormat value={currentRow.amount} thousandSeparator prefix={'$'} displayType="text" style={{ color: "grey" }} />
      }
    },
    {
      field: 'cashAmount', headerName: 'Cash Amount', width: 90, align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <b><NumericFormat value={currentRow.cashAmount} thousandSeparator prefix={'$'} displayType="text" /></b>
      }
    },
    {
      field: 'otherAmount', headerName: 'Other', width: 90, align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <b><NumericFormat value={currentRow.otherAmount} thousandSeparator prefix={'$'} displayType="text" /></b>
      }
    },
    {
      field: 'creditCardAmount', headerName: 'Credit Card Amount', width: 90, align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <NumericFormat value={currentRow.creditCardAmount} thousandSeparator prefix={'$'} displayType="text" style={{ color: "grey" }} />
      }
    },
  ];

  const groupByColumns = [
    {
      field: 'actualPaymentDate', headerName: 'Payment Date', width: 200, align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        if (currentRow.actualPaymentDate)
          return parseISO(currentRow.actualPaymentDate).toLocaleDateString('en-us', options)
        return null;
      }
    },
    {
      field: 'totalAmount', headerName: 'Total Amount', width: 180, align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <NumericFormat value={currentRow.totalAmount} thousandSeparator prefix={'$'} displayType="text" style={{ color: "grey" }} />
      }
    },
    {
      field: 'totalNonCreditAmount', headerName: 'Total Non-Credit Amount', width: 180, align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <b><NumericFormat value={currentRow.totalNonCreditAmount} thousandSeparator prefix={'$'} displayType="text" /></b>
      }
    },
    {
      field: 'cashAmount', headerName: 'Cash Amount', width: 180, align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <b><NumericFormat value={currentRow.cashAmount} thousandSeparator prefix={'$'} displayType="text" style={{ color: "grey" }} /></b>
      }
    },
    {
      field: 'otherAmount', headerName: 'Other non-credit payment type', width: 240, align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <b><NumericFormat value={currentRow.otherAmount} thousandSeparator prefix={'$'} displayType="text" style={{ color: "grey" }} /></b>
      }
    },
    {
      field: 'creditCardAmount', headerName: 'Credit Card Amount', width: 220, align: 'left',
      renderCell: (params) => {
        const currentRow = params.row;
        return <NumericFormat value={currentRow.creditCardAmount} thousandSeparator prefix={'$'} displayType="text" style={{ color: "grey" }} />
      }
    },
  ];

  const getPaymentDetailsByDateRange = async (searchStartDate, searchEndDate) => {
    const dateColumnFilter = 'actualPaymentDate';
    await axios.post(`${url}/payments/allPaymentsinDateRange`, {
      searchStartDate,
      searchEndDate,
      dateColumnFilter
    }).then(async (responseForTenancyDetails) => {
      console.log(responseForTenancyDetails.data);
      if (responseForTenancyDetails && responseForTenancyDetails.data)
        setReportSummary(responseForTenancyDetails.data);
    });
  }

  const preProcess = (data, missingData) => {
    let resultArray = [];
    let receiptNumberDict = {};
    let rangeDict = {};
    let minValue = null, maxValue = null;
    for (let itr in data) {
      const receiptNumber = data[itr].receiptNumber;
      if (receiptNumber) {
        if (receiptNumber in receiptNumberDict) {
          receiptNumberDict[receiptNumber] += 1;
        } else {
          receiptNumberDict[receiptNumber] = 1;
        }
        rangeDict[receiptNumber] = 1;
        let strValue = receiptNumber.toString();
        if (strValue.length === 6) {
          if (minValue == null) {
            minValue = receiptNumber;
            maxValue = receiptNumber;
          } else {
            if (minValue > receiptNumber) {
              minValue = receiptNumber;
            }
            if (maxValue < receiptNumber) {
              maxValue = receiptNumber;
            }
          }
        }
      }
    }

    totalCash = 0;
    totalOther = 0;
    for (let itr in data) {
      const currentRow = data[itr];
      let tempJson = data[itr];
      let amount = 0;
      if (currentRow.cashAmount) {
        let ca = parseInt(currentRow.cashAmount);
        amount = amount + ca;
        totalCash += ca;
      }
      if (currentRow.creditCardAmount)
        amount += parseInt(currentRow.creditCardAmount);
      if (currentRow.otherAmount) {
        let oa = parseInt(currentRow.otherAmount);
        amount += oa;
        totalOther += oa;
      }
      tempJson["amount"] = amount;
      tempJson["numberOfDaysStayed"] = dateDifference(currentRow.checkin, currentRow.firstArrivalDate) + 1;
      const dateDiff_checkin = dateDifference(currentRow.firstArrivalDate, currentRow.checkin);
      const dateDiff_checkout = dateDifference(currentRow.firstArrivalDate, currentRow.checkout);

      if (dateDiff_checkin <= 183 && dateDiff_checkout <= 183) {
        tempJson["taxableAmount"] = Math.round((amount / (1 + salesTax + tourismTax)) * 100) / 100;
        tempJson["exemptAmount"] = 0;
      } else if (dateDiff_checkin > 183 && dateDiff_checkout > 183) {
        tempJson["taxableAmount"] = 0;
        tempJson["exemptAmount"] = amount;
      } else {
        const ndays = dateDifference(currentRow.checkin, currentRow.checkout);
        const init = 183 - dateDiff_checkin;
        const amo = (amount / ndays) * init;
        tempJson["taxableAmount"] = Math.round((amo / (1 + salesTax + tourismTax)) * 100) / 100;
        tempJson["exemptAmount"] = Math.round(((amount / ndays) * (ndays - init)) * 100) / 100;
      }
      tempJson["salesTax"] = Math.round((tempJson["taxableAmount"] * 0.07) * 100) / 100;
      tempJson["tourismTax"] = Math.round((tempJson["taxableAmount"] * 0.05) * 100) / 100;
      totalTaxableAmount += tempJson["taxableAmount"];
      totalExemptAmount += tempJson["exemptAmount"];
      totalSalesTax += tempJson["salesTax"];
      totalTourismTax += tempJson["tourismTax"];

      const receiptNumber = currentRow.receiptNumber;
      if (receiptNumber) {
        if (receiptNumber in receiptNumberDict && receiptNumberDict[receiptNumber] > 1) {
          tempJson["receiptNumberStatus"] = "duplicate";
        } else {
          tempJson["receiptNumberStatus"] = "all-okay";
          if (amount < 0) {
            tempJson["receiptNumberStatus"] = "refunded";
          }
        }
        let strValue = receiptNumber.toString();
        if (strValue.length != 6) {
          tempJson["receiptNumberStatus"] = "wrong-number";
        }
      } else {
        tempJson["receiptNumberStatus"] = "no-entry";
      }
      tempJson["difference"] = currentRow.rate - amount;

      if (!(currentRow.roomid in roomsToTotalsDict)) {
        roomsToTotalsDict[currentRow.roomid] = {
          total: 0,
          taxableAmount: 0,
          exemptAmount: 0,
          salesTax: 0,
          tourismTax: 0
        };
      }
      roomsToTotalsDict[currentRow.roomid].total += amount;
      roomsToTotalsDict[currentRow.roomid].taxableAmount += tempJson["taxableAmount"];
      roomsToTotalsDict[currentRow.roomid].exemptAmount += tempJson["exemptAmount"];
      roomsToTotalsDict[currentRow.roomid].salesTax += tempJson["salesTax"];
      roomsToTotalsDict[currentRow.roomid].tourismTax += tempJson["tourismTax"];

      resultArray.push(tempJson);
    }
    for (let itr in missingData) {
      let tempJson = missingData[itr];
      tempJson["difference"] = missingData[itr].rate - (missingData[itr].amount ? missingData[itr].amount : 0);
      resultArray.push(tempJson);
    }

    console.log("Roomwise statistics");
    console.log(roomsToTotalsDict);
    return resultArray;
  }

  function processDataGroupByDate(data) {
    let dictionary = {};
    totalCash = 0;
    totalOther = 0;
    totalCreditAmount = 0;
    for (let itr in data) {
      const currentRow = data[itr];
      let amount = 0;
      if (!(currentRow.actualPaymentDate in dictionary)) {
        dictionary[currentRow.actualPaymentDate] = {
          totalAmount: 0, cashAmount: 0, otherAmount: 0, creditCardAmount: 0, totalNonCreditAmount: 0
        };
      }
      if (currentRow.cashAmount) {
        let ca = parseInt(currentRow.cashAmount);
        amount = amount + ca;
        totalCash += ca;
        dictionary[currentRow.actualPaymentDate].cashAmount += ca;
      }
      if (currentRow.creditCardAmount) {
        let oa = parseInt(currentRow.creditCardAmount);
        amount += oa;
        totalCreditAmount += oa;
        dictionary[currentRow.actualPaymentDate].creditCardAmount += oa;
      }
      if (currentRow.otherAmount) {
        let oa = parseInt(currentRow.otherAmount);
        amount += oa;
        totalOther += oa;
        dictionary[currentRow.actualPaymentDate].otherAmount += oa;
      }
      dictionary[currentRow.actualPaymentDate].totalAmount += amount;
    }
    let resultArray = [];
    Object.keys(dictionary).forEach((key) => {
      console.log(key + ": " + dictionary[key]);
      let tempJson = dictionary[key];
      tempJson["actualPaymentDate"] = key;
      tempJson["totalNonCreditAmount"] = (dictionary[key].cashAmount + dictionary[key].otherAmount);
      resultArray.push(tempJson);
    });
    console.log(dictionary);
    return resultArray;
  }

  const MissingReceiptNumberWarning = () => {
    return <>
      <Dialog
        open={missingReceiptWarning}
        onClose={(e) => {
          setMissingReceiptWarning(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            The Missing Receipt Number
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </>
  }

  function CustomPagination(props) {
    return <>
      <b> Cash: &nbsp;
        <NumericFormat value={Math.round((totalCash) * 100) / 100} thousandSeparator prefix={'$'} displayType="text" /></b>
      <b> Other: &nbsp;
        <NumericFormat value={Math.round((totalOther) * 100) / 100} thousandSeparator prefix={'$'} displayType="text" /></b>
      <b> Credit: &nbsp;
        <NumericFormat value={Math.round((totalCreditAmount) * 100) / 100} thousandSeparator prefix={'$'} displayType="text" /></b>
      <b> Total: &nbsp;
        <NumericFormat value={Math.round((totalCash + totalOther + totalCreditAmount) * 100) / 100} thousandSeparator prefix={'$'} displayType="text" /></b>
      <GridPagination  {...props} />
    </>;
  }

  const classes = useStyles();
  const totalAmount = totalCash + totalOther + totalCreditAmount;

  return (
    <>
      <Header />
      <Container maxWidth="xl" sx={{ mt: 3, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', 
                                      alignItems: 'center', mb: 3,
                                        pb: 2,borderBottom: 1,borderColor: "divider", }}>
            <Typography variant="h6" fontWeight="600">
              Cash Collection Report
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <ToggleButtonGroup
                value={displayType}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) {
                    setDisplayType(newValue);
                  }
                }}
                aria-label="display type"
                size="small"
              >
                <ToggleButton value="daily" aria-label="daily">
                  Daily
                </ToggleButton>
                <ToggleButton value="weekly" aria-label="weekly">
                  Weekly
                </ToggleButton>
                <ToggleButton value="monthly" aria-label="monthly">
                  Monthly
                </ToggleButton>
                <ToggleButton value="details" aria-label="details">
                  Details
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={searchStartDate}
                onChange={(e) => setSearchStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180 }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={searchEndDate}
                onChange={(e) => setSearchEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180 }}
              />
              <Button
                variant="contained"
                onClick={(e) => {
                  getPaymentDetailsByDateRange(searchStartDate, searchEndDate);
                }}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Search
              </Button>
            </Stack>
          </Box>

          

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ bgcolor: 'success.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                      <MoneyIcon />
                    </Box>
                    <Box>
                      <Typography variant="body" color="text.secondary">Cash</Typography>
                      <Typography variant="h6" fontWeight="600">
                        <NumericFormat value={totalCash} thousandSeparator prefix={'$'} displayType="text" />
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ bgcolor: 'info.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                      <BankIcon />
                    </Box>
                    <Box>
                      <Typography variant="body" color="text.secondary">Other</Typography>
                      <Typography variant="h6" fontWeight="600">
                        <NumericFormat value={totalOther} thousandSeparator prefix={'$'} displayType="text" />
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ bgcolor: 'warning.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                      <CreditCardIcon />
                    </Box>
                    <Box>
                      <Typography variant="body" color="text.secondary">Credit</Typography>
                      <Typography variant="h6" fontWeight="600">
                        <NumericFormat value={totalCreditAmount} thousandSeparator prefix={'$'} displayType="text" />
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={2}>
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 1.5, borderRadius: 2 }}>
                      <ReceiptIcon />
                    </Box>
                    <Box>
                      <Typography variant="body" color="text.secondary">Total</Typography>
                      <Typography variant="h6" fontWeight="600">
                        <NumericFormat value={totalAmount} thousandSeparator prefix={'$'} displayType="text" />
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Paper elevation={2} sx={{ borderRadius: 2 }}>
          <Box sx={{ height: 500, width: '100%' }}>
            {displayType === "details" ?
              <DataGrid
                rows={preProcess(reportSummary, missingPaymentsReportSummary)}
                columns={columns}
                getRowId={(row) => row.paymentId + ' ' + row.id}
                slots={{
                  pagination: CustomPagination,
                }}
                sx={{
                  border: "none",
                  "& .MuiDataGrid-cell": {
                    borderBottom: 1,
                    borderColor: "divider",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    bgcolor: "grey.50",
                    borderBottom: 2,
                    borderColor: "divider",
                  },
                }}
                getRowClassName={(params) =>
                  params.row.receiptNumberStatus == "duplicate" ? classes.duplicateRow :
                    params.row.receiptNumberStatus == "no-entry" ? classes.noEntryRow :
                      params.row.receiptNumberStatus == "wrong-number" ? classes.WrongNumberRow :
                        params.row.receiptNumberStatus == "refunded" ? classes.refundedRow :
                          classes.normalRow
                }
              /> :
              <DataGrid
                rows={processDataGroupByDate(reportSummary)}
                columns={groupByColumns}
                getRowId={(row) => row.actualPaymentDate}
                slots={{
                  pagination: CustomPagination,
                }}
                sx={{
                  border: "none",
                  "& .MuiDataGrid-cell": {
                    borderBottom: 1,
                    borderColor: "divider",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    bgcolor: "grey.50",
                    borderBottom: 2,
                    borderColor: "divider",
                  },
                }}
              />}
          </Box>
        </Paper>
      </Container>
      {makePaymentModal && <MakePayment value={{ paymentDetails: selectedRow }} />}
      {missingReceiptWarning && <MissingReceiptNumberWarning />}
    </>
  );
}

export default CashCollectionReport;